import { useEffect, useState } from 'react'
import {
  api, can, ApiError, prettyPhone,
  type Contact, type ContactHistory,
} from '../lib/api'
import { SaveNumber } from './SaveNumber'
import { PastCallRows } from './CallerHistory'
import {
  AddressFields, BLANK_ADDRESS, addressBody, isEmpty, type Address,
} from './AddressFields'

/** The caller's card, opened when the phone rings.
 *
 *  A RINGING PHONE IS NOT AN INCIDENT.
 *
 *  The strip used to go straight from "incoming" to the intake form, which
 *  decides for the dispatcher that this call is a job. Most calls are not: a
 *  wrong number, somebody checking a time, a caller ringing back about a call
 *  that already exists. Opening a half-written incident for each of those
 *  leaves records nobody wanted and has to be undone mid-call.
 *
 *  So the phone ringing opens the CALLER, not a call. Who they are, where they
 *  are, what they have rung about before — and one button that turns this into
 *  an incident when it turns out to be one.
 *
 *  IT STAYS AFTER THEY HANG UP.
 *
 *  The dispatcher answers on the desk phone and takes the address by ear. If
 *  the card disappears the moment the caller rings off, it vanishes at exactly
 *  the moment he needs it — so a finished call sits here for a few minutes,
 *  marked as finished.
 */

export type RingingCall = {
  linkedid: string
  caller_number: string
  caller_name: string | null
  contact_id: string | null
  contact_name: string | null
  dialled: string | null
  extension: string | null
  started_at: string
  answered_at: string | null
  ended_at: string | null
  live: boolean
  never_rang: boolean
}

export function CallerCard({
  call, onTake, onClose, onAdded,
}: {
  call: RingingCall
  /** Turn this into an incident. The card knows the caller; intake gets them
   *  filled in rather than typed again while somebody is talking. */
  onTake: (caller: {
    caller_name: string
    caller_phone: string
    contact_id: string | null
    /** The caller's own address, carried so intake can OFFER it. Whether it
     *  becomes the incident's address is decided by the relationship, not
     *  here. */
    caller_address: string | null
    /** The name the phone system itself gave (079), apart from the book's. */
    pbx_name?: string | null
  }) => void
  onClose: () => void
  onAdded: () => void
}) {
  const [contact, setContact] = useState<Contact | null>(null)
  const [count, setCount] = useState<number>(0)
  /** Everyone this number belongs to. A shared home line is legitimate; what
   *  is not legitimate is picking one of them and showing his address as if it
   *  were settled. */
  const [holders, setHolders] = useState<{ id: string; display_name: string | null }[]>([])
  /** The note on the number that rang, when it is not his main one -- "Wife"
   *  (25 Oct 2026). */
  const [savedAs, setSavedAs] = useState<string | null>(null)
  const [history, setHistory] = useState<ContactHistory | null>(null)
  /** WHY THIS IS NOT `history === null`. Null is the LOADING state, so the
   *  catch that set history back to null on a failure rendered "Loading…"
   *  for ever — a 403 on `calls.incident_record.caller_history` looked
   *  exactly like a slow server, mid-ring, and the dispatcher waited for a
   *  fetch that had already finished failing. The failure needs somewhere of
   *  its own to live before it can be said out loud. */
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  /** Anything this card tried to WRITE and could not. The card had no error
   *  surface at all, which is how the address save below could fail in total
   *  silence; on the screen that opens mid-ring a refusal has to be a
   *  sentence, because the alternative is a dispatcher deciding for himself
   *  whether the thing he just typed was stored. */
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  /** Taking an address down for a caller who has none, while he is still on
   *  the line. This is the moment the organization learns it, and there was no
   *  way to record it without leaving the call. */
  const [takingAddress, setTakingAddress] = useState(false)
  const [address, setAddress] = useState<Address>({ ...BLANK_ADDRESS })
  const [savingAddress, setSavingAddress] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!call.caller_number) {
      setContact(null)
      return
    }
    api.contactLookup(call.caller_number)
      .then((r) => {
        if (cancelled) return
        setContact(r.match)
        setCount(r.incident_count)
        setHolders(r.holders ?? [])
        // ONLY A NUMBER SAVED FROM A CALL AS ANOTHER NUMBER ("Wife"): the
        // phone book's own second numbers -- "other", "office", an imported
        // alternate -- are his, and must not empty his name (review).
        setSavedAs(r.matched_number && !r.matched_number.is_primary
          && r.matched_number.source === 'call'
          ? r.matched_number.label?.trim() || null : null)
      })
      .catch(() => { if (!cancelled) setContact(null) })
    return () => { cancelled = true }
  }, [call.caller_number, call.linkedid])

  useEffect(() => {
    if (!showHistory || !contact || history) return
    setHistoryError(null)
    api.contactHistory(contact.id)
      .then(setHistory)
      // THREE FAILURES THAT NEED THREE DIFFERENT ANSWERS, and the server
      // already writes all three: `deps.needs` names the refused act ("Seeing
      // caller history is not permitted for your role"), the route answers
      // "No such caller" when the record has gone since the card opened, and
      // `request` answers "Cannot reach the server" when the fetch itself
      // never landed. Carrying that sentence through is what stops the first
      // one — a permission somebody has to grant — reading like the third,
      // which is a dispatcher's cue to reload and lose the card.
      .catch((e) => setHistoryError(
        e instanceof ApiError ? e.message
          : 'Could not load their previous calls.'))
  }, [showHistory, contact, history])

  const known = !!contact
  const ambiguous = holders.length > 1
  /** One label, drawn whether or not the role may open the detail behind it —
   *  so the two cannot drift into saying different numbers. */
  const historyLabel = count === 0
    ? 'No calls on record'
    : `${count} previous call${count === 1 ? '' : 's'}`
  const elapsed = call.ended_at
    ? null
    : Math.max(0, Math.round((Date.now() - new Date(call.started_at).getTime()) / 1000))

  return (
    <div className={`callercard ${call.live ? '' : 'finished'}`}>
      <div className="cc-head">
        <span className={`cc-state ${call.live ? 'ringing' : 'over'}`}>
          {call.live
            ? call.answered_at ? 'On the phone' : 'Incoming'
            : 'Call ended'}
        </span>
        <div className="cc-who">
          <b>{contact?.display_name ?? call.caller_name ?? 'Unknown caller'}</b>
          <span className="mono">
            {call.caller_number ? prettyPhone(call.caller_number) : 'number withheld'}
          </span>
        </div>
        {call.dialled && (
          <span className="dim">rang {prettyPhone(call.dialled)}</span>
        )}
        {call.extension && <span className="dim">ext {call.extension}</span>}
        {elapsed !== null && <span className="mono dim">{elapsed}s</span>}
        <span className="spacer" />
        <button className="ghost tiny" onClick={onClose}>Dismiss</button>
      </div>

      <div className="cc-body">
        {/* Whatever this card tried to write and could not, at the top where
            somebody looking at the caller cannot miss it. Nothing on this
            screen may fail quietly: the dispatcher is on the phone, and a
            control that un-spins with no message leaves him to guess whether
            the address he just took down was kept. */}
        {error && <div className="notice">{error}</div>}
        {/* What is useful while somebody is talking, and nothing else.
            Section 06: history is one click away because it is a distraction
            at the moment attention is scarcest. */}
        {known ? (
          <>
            {/* Section 06: while the caller is talking, the useful things are
                who they are, WHERE THEY ARE, and the number. The address is
                one of the three, so it is on the card and not behind a click. */}
            {/* Two people on one number. Say so, and offer neither address:
                we do not know whose house he is standing in. */}
            {/* A NUMBER SAVED UNDER HIM AS "WIFE" IS NOT HIM ON THE LINE
                (25 Oct 2026, the customer's Q1 A): said, and Take the call
                leaves the name for the dispatcher to ask. */}
            {savedAs && !ambiguous && (
              <div className="cc-savedas">This is the number saved as “{savedAs}”</div>
            )}
            {ambiguous ? (
              <div className="cc-ambiguous">
                This number belongs to{' '}
                {holders.map((h) => h.display_name).filter(Boolean).join(' and ')}.
                Ask who is calling — no address is being offered.
              </div>
            ) : contact!.address && (
              <div className="cc-address">
                {contact!.address}
                {/* The apartment again, on its own, because it is the part
                    somebody standing at a door needs and it disappears into
                    the middle of a long line. */}
                {contact!.unit && (
                  <span className="cc-unit">Apt {contact!.unit}</span>
                )}
                {contact!.entry_codes && (
                  <span className="cc-unit">{contact!.entry_codes}</span>
                )}
              </div>
            )}
            {/* Entry codes, a dog, where the gate is. These are the reason
                site notes exist, and the moment they matter is now. */}
            {/* The date and the author come too. A note about a dog written
                in 2023 is not the same claim as one written last week, and
                section 06 names that as the mitigation for notes that
                accumulate and contradict. */}
            {contact!.site_notes.map((n) => (
              <p className={`cc-notes ${n.severity}`} key={n.id}>
                {n.body}
                {n.author_label && (
                  <span className="by">
                    {' '}— {n.author_label}
                    {n.written_at
                      ? `, ${new Date(n.written_at).toLocaleDateString()}`
                      : ''}
                  </span>
                )}
              </p>
            ))}
            <div className="cc-facts">
              {contact!.language && <span className="tag">{contact!.language}</span>}
              {/* Every other number he rings from, so the dispatcher can see
                  this is the same man who called from the office last week. */}
              {contact!.phones
                .filter((p) => p.phone_e164 !== call.caller_number
                            && prettyPhone(p.phone_e164) !== prettyPhone(call.caller_number))
                .map((p) => (
                  <span className="dim" key={p.id}>
                    also {prettyPhone(p.phone_e164)}
                    {p.label ? ` (${p.label})` : ''}
                  </span>
                ))}
              {/* THE COUNT IS SAID TO EVERY ROLE; ONLY THE DETAIL BEHIND IT IS
                  GATED. `POST /api/contacts/lookup` is deliberately ungated —
                  it is part of answering the phone — and it already returns
                  `incident_count`, so nothing is disclosed here that this role
                  could not already see. Suppressing the number would cost more
                  than it saves: a caller who has rung eleven times would render
                  identically to one who has never rung at all, and that is not
                  a missing button, it is a false statement made mid-ring. What
                  the button opens is `GET /api/contacts/{id}/history`, gated on
                  `calls.incident_record.caller_history`, so without the key it
                  is drawn as the fact it is rather than as a control that looks
                  pressable and answers 403. */}
              {/* NOT ON A SHARED LINE: the contact here is whoever was edited
                  last, and his past calls and closing notes are not the
                  caller's until somebody asks who is calling. */}
              {can('calls.incident_record.caller_history') && !ambiguous ? (
                <button className="chip" onClick={() => setShowHistory(!showHistory)}>
                  {historyLabel}
                </button>
              ) : (
                <span className="dim">{historyLabel}</span>
              )}
            </div>
            {!ambiguous && !contact!.address && !takingAddress && (
              <div className="cc-unknown">
                <span>No address on file.</span>
                {/* The SENTENCE stays for every role — that there is no address
                    is a fact the dispatcher has to know before he asks for one.
                    Only the button goes: it saves through
                    `PATCH /api/contacts/{id}`, which is gated on
                    `phonebook.edit`, and a button that collects an address by
                    ear and then refuses it is worse than no button. */}
                {can('phonebook.edit') && (
                  <button className="ghost tiny"
                          onClick={() => setTakingAddress(true)}>
                    Take it down
                  </button>
                )}
              </div>
            )}
            {takingAddress && (
              <div className="detail">
                <AddressFields value={address} onChange={setAddress} />
                <div className="btnrow">
                  <button
                    className="primary compact"
                    disabled={savingAddress || isEmpty(address)}
                    onClick={async () => {
                      setSavingAddress(true)
                      setError(null)
                      try {
                        const saved = await api.contactUpdate(contact!.id,
                                                              addressBody(address))
                        setContact(saved)
                        setTakingAddress(false)
                        onAdded()
                      } catch (e) {
                        // THERE WAS NO CATCH HERE, and the failure that left is
                        // the worst shape a failure can take on this screen:
                        // the rejection went unhandled, `finally` put the label
                        // back to "Save to their record", and NOTHING appeared.
                        // A dispatcher who had just taken an address down by
                        // ear, on the phone, had no way to tell a saved address
                        // from a refused one — and this route is gated on
                        // `phonebook.edit`, so for a role without it that
                        // silence was the only answer it would ever give. The
                        // fallback tells him to keep the address himself,
                        // because the words are still in his head now and will
                        // not be after the call.
                        setError(e instanceof ApiError ? e.message
                          : 'That address was not saved. Keep it in front of '
                            + 'you — it is not on their record.')
                      } finally {
                        setSavingAddress(false)
                      }
                    }}
                  >
                    {savingAddress ? 'Saving…' : 'Save to their record'}
                  </button>
                  <button className="ghost"
                          onClick={() => setTakingAddress(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {contact!.notes && <p className="cc-notes">{contact!.notes}</p>}
            {showHistory && (
              <div className="detail">
                {historyError ? (
                  <div className="notice">{historyError}</div>
                ) : history === null ? (
                  <div className="dim">Loading…</div>
                ) : (
                  <>
                    <div className="chips">
                      {history.by_relationship.map((b) => (
                        <span className="chip" key={b.relationship}>
                          {b.relationship} · {b.calls}
                        </span>
                      ))}
                    </div>
                    {/* With how each one ended -- see `PastCallRows`. */}
                    <PastCallRows incidents={history.incidents} limit={6} />
                  </>
                )}
              </div>
            )}
          </>
        ) : adding ? (
          // The same choice as the call log: this may be a new person, or the
          // third number of somebody already here.
          <SaveNumber
            phone={call.caller_number}
            suggestedName={call.caller_name ?? ''}
            onCancel={() => setAdding(false)}
            onDone={(c) => {
              setContact(c)
              setAdding(false)
              onAdded()
            }}
          />
        ) : (
          <div className="cc-unknown">
            <span>Not in the phone book.</span>
            {/* BOTH KEYS, BECAUSE THE FORM BEHIND THIS BUTTON IS TWO ROUTES.
                `SaveNumber` opens on "add to someone already here", which is
                `POST /api/contacts/{id}/phones` and `phonebook.edit`, and its
                other tab creates a caller through `POST /api/contacts`, which
                is `phonebook.add`. A role holding either has something it can
                finish here; a role holding neither would be filling in a name
                and an address mid-call for a 403. Two exact keys and not
                `canAny('phonebook')` — view and bulk import are in that branch
                and neither of them can save this caller. */}
            {call.caller_number && (can('phonebook.add') || can('phonebook.edit')) && (
              <button className="ghost tiny" onClick={() => setAdding(true)}>
                Add them
              </button>
            )}
          </div>
        )}
      </div>

      <div className="cc-acts">
        {/* The one thing that turns a phone call into a job. Deliberately not
            automatic: most calls are not incidents. */}
        <button
          className="btn primary"
          onClick={() =>
            onTake({
              // HIS NAME ONLY WHEN IT IS HIS NUMBER: one saved as "Wife"
              // leaves it to be asked, and the call is still on his record.
              caller_name: savedAs ? '' : (contact?.display_name ?? call.caller_name ?? ''),
              caller_phone: call.caller_number,
              contact_id: contact?.id ?? null,
              // The phone system's own name (079): only this is kept for good.
              pbx_name: call.caller_name ?? null,
              // Withheld when two people share the number: intake must not
              // offer an address we cannot attribute.
              caller_address: ambiguous ? null : (contact?.address ?? null),
            })
          }
        >
          Take the call
        </button>
        <span className="dim">
          Starts an incident and broadcasts it. Nothing goes out until you do.
        </span>
      </div>
    </div>
  )
}
